/**
 * @fileoverview In-App Notifications Hook
 * 
 * Manages real-time in-app notifications for users. Notifications appear in
 * the application UI to alert users of important events, updates, and actions
 * requiring attention. Includes real-time updates via Supabase subscriptions.
 * 
 * Notification System:
 * - Real-time notifications via Supabase Realtime subscriptions
 * - Toast notifications for immediate user feedback
 * - Persistent notification list in UI
 * - Unread count badge for notification icon
 * - Mark as read functionality (single and bulk)
 * - Delete notifications
 * 
 * Notification Types:
 * - info: General information updates
 * - success: Successful operations and completions
 * - warning: Warnings requiring attention
 * - error: Error conditions and failures
 * 
 * Notification Categories:
 * - rfq: RFQ related (new matches, questions, closures)
 * - bid: Bid related (submissions, reviews, awards)
 * - job: Job related (status changes, milestones, completions)
 * - milestone: Milestone reminders and updates
 * - review: Review requests and completions
 * - general: General platform updates and announcements
 * 
 * Notification Structure:
 * Each notification includes:
 * - Title: Brief subject line
 * - Message: Detailed notification text
 * - Type: Visual styling (info, success, warning, error)
 * - Category: Grouping and filtering
 * - Related entity: Link to source (job, RFQ, etc.)
 * - Action URL: Deep link to relevant page
 * - Read status: Tracking user engagement
 * 
 * Real-time Updates:
 * - Automatic subscription to user's notifications
 * - New notifications appear instantly
 * - Toast displayed for immediate awareness
 * - Unread count updates automatically
 * - Clean disconnection on unmount
 * 
 * Key Features:
 * - Real-time notification delivery
 * - Unread count tracking
 * - Mark single notification as read
 * - Mark all notifications as read
 * - Delete notifications
 * - Toast notifications on new messages
 * - Automatic cleanup and subscription management
 * 
 * @module useInAppNotifications
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

/**
 * In-app notification record
 * 
 * Represents a single notification displayed in the user's notification center.
 * Includes all information needed to display, categorize, and link the notification.
 */
export interface Notification {
  id: string;
  user_id: string;
  organization_id?: string | null;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  category: string;
  related_entity_type?: string | null;
  related_entity_id?: string | null;
  action_url?: string | null;
  is_read: boolean;
  read_at?: string | null;
  created_at: string;
}

/**
 * Hook for managing in-app notifications
 * 
 * Provides real-time notification management with automatic subscriptions,
 * read/unread tracking, and deletion capabilities. Notifications are loaded
 * on mount and updated in real-time as new notifications arrive.
 * 
 * Real-time Subscription:
 * - Subscribes to INSERT events for new notifications
 * - Subscribes to UPDATE events for read status changes
 * - Displays toast for new notifications
 * - Automatically updates notification list
 * - Cleans up subscription on unmount
 * 
 * Notification Limit:
 * - Fetches most recent 50 notifications
 * - Older notifications archived but not deleted
 * - Prevents UI performance issues with large lists
 * 
 * @returns Object with notifications, unread count, and management functions
 * 
 * @example
 * ```tsx
 * function NotificationCenter() {
 *   const {
 *     notifications,
 *     unreadCount,
 *     loading,
 *     markAsRead,
 *     markAllAsRead,
 *     deleteNotification
 *   } = useInAppNotifications();
 * 
 *   if (loading) return <Spinner />;
 * 
 *   return (
 *     <div>
 *       <h2>Notifications ({unreadCount} unread)</h2>
 *       <Button onClick={markAllAsRead}>Mark All Read</Button>
 *       
 *       {notifications.map(notification => (
 *         <NotificationItem
 *           key={notification.id}
 *           notification={notification}
 *           onRead={() => markAsRead(notification.id)}
 *           onDelete={() => deleteNotification(notification.id)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Notification badge with unread count
 * function NotificationBadge() {
 *   const { unreadCount } = useInAppNotifications();
 * 
 *   return (
 *     <button>
 *       <Bell />
 *       {unreadCount > 0 && (
 *         <span className="badge">{unreadCount}</span>
 *       )}
 *     </button>
 *   );
 * }
 * ```
 */
export const useInAppNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  
  // Track last realtime event to enable smart polling
  const lastRealtimeEventRef = useRef<number>(Date.now());

  /**
   * Fetches user's notifications
   * 
   * Loads most recent 50 notifications for the current user, ordered by
   * creation date descending. Calculates unread count from results.
   */
  const fetchNotifications = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .neq('category', 'message')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching notifications:', error);
        return;
      }

      setNotifications((data || []) as Notification[]);
      setUnreadCount(data?.filter(n => !n.is_read).length || 0);
    } catch (error) {
      console.error('Error in fetchNotifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Sets up real-time subscription and initial load
   * 
   * Subscribes to notification changes for current user:
   * - INSERT: New notifications → add to list and show toast
   * - UPDATE: Read status changes → update notification in list
   * 
   * Cleanup on unmount removes subscription to prevent memory leaks.
   */
  useEffect(() => {
    let mounted = true;
    let channel: any;

    const setupRealtimeSubscription = async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user || !mounted) return;

      const userId = data.user.id;

      channel = supabase
        .channel(`notifications-${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            if (!mounted) return;
            // Update last realtime event timestamp
            lastRealtimeEventRef.current = Date.now();
            
            const newNotification = payload.new as Notification;
            
            // Skip message notifications - they go to MessagesDropdown
            if (newNotification.category === 'message') return;
            
            setNotifications(prev => [newNotification, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Show toast notification for immediate awareness
            toast({
              title: newNotification.title,
              description: newNotification.message,
            });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            if (!mounted) return;
            // Update last realtime event timestamp
            lastRealtimeEventRef.current = Date.now();
            
            const updatedNotification = payload.new as Notification;
            setNotifications(prev =>
              prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
            );
            fetchNotifications(); // Refetch to get updated unread count
          }
        )
        .subscribe();
    };

    fetchNotifications();
    setupRealtimeSubscription();

    return () => {
      mounted = false;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [fetchNotifications, toast]);

  /**
   * Smart polling fallback - only polls if no realtime event received in 2+ minutes
   * Checks every 60 seconds, but only fetches if realtime appears stale
   */
  useEffect(() => {
    const REALTIME_STALE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes
    const POLL_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

    const interval = setInterval(() => {
      const timeSinceLastEvent = Date.now() - lastRealtimeEventRef.current;
      if (timeSinceLastEvent > REALTIME_STALE_THRESHOLD_MS) {
        console.log('Realtime appears stale, polling for notifications...');
        fetchNotifications();
        // Reset the timestamp after polling to prevent continuous polling
        lastRealtimeEventRef.current = Date.now();
      }
    }, POLL_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  /**
   * Refresh notifications when user returns to the browser tab
   * Ensures count is always current when user focuses the app
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchNotifications]);

  /**
   * Marks a single notification as read
   * 
   * Updates notification's is_read flag and sets read_at timestamp.
   * Updates local state optimistically for instant UI feedback.
   * 
   * @param notificationId - ID of notification to mark as read
   * 
   * @example
   * ```typescript
   * // Mark notification as read when user clicks it
   * const handleNotificationClick = async (notification) => {
   *   await markAsRead(notification.id);
   *   // Navigate to action URL
   *   router.push(notification.action_url);
   * };
   * ```
   */
  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error in markAsRead:', error);
    }
  };

  /**
   * Marks all notifications as read
   * 
   * Bulk operation to mark all unread notifications as read. Useful for
   * "Mark all as read" button in notification center.
   * 
   * @example
   * ```typescript
   * // Mark all button
   * <Button onClick={markAllAsRead}>
   *   Mark All as Read
   * </Button>
   * ```
   */
  const markAllAsRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        return;
      }

      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error in markAllAsRead:', error);
    }
  };

  /**
   * Deletes a notification
   * 
   * Permanently removes notification from database and local state.
   * If notification was unread, decrements unread count.
   * 
   * @param notificationId - ID of notification to delete
   * 
   * @example
   * ```typescript
   * // Delete button in notification item
   * <IconButton onClick={() => deleteNotification(notification.id)}>
   *   <X />
   * </IconButton>
   * ```
   */
  const deleteNotification = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        console.error('Error deleting notification:', error);
        return;
      }

      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error in deleteNotification:', error);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch: fetchNotifications
  };
};
